import {
  LineExtensionContact,
  LineExtensionContactV2,
  LineExtensionPayload,
  PhoneCall,
} from '@mangovoice/types'

// ============================================================================
// Contact Helper Functions (moved from libraries/types/src/phone.ts)
// ============================================================================

/**
 * Type guard to check if a contact is v2
 */
export const isV2Contact = (
  contact: LineExtensionContact | undefined,
): contact is LineExtensionContactV2 => {
  return contact?.version === 2
}

/**
 * Gets the first source ID from the contact's sources array (v2 only).
 */
const getFirstSourceId = (
  contact: LineExtensionContact | undefined,
): string | undefined => {
  if (!contact || !isV2Contact(contact)) return undefined
  if (!contact.sources || contact.sources.length === 0) return undefined
  return contact.sources[0].id
}

/**
 * Gets the external identifier that matches the first source from the contact's sources array (v2 only).
 * Returns undefined if no matching identifier is found.
 */
const getMatchingExternalIdentifier = (
  contact: LineExtensionContact | undefined,
): LineExtensionContactV2['external_identifiers'][0] | undefined => {
  if (!isV2Contact(contact)) return undefined
  const firstSourceId = getFirstSourceId(contact)
  if (!firstSourceId) return undefined
  if (
    !contact?.external_identifiers ||
    contact.external_identifiers.length === 0
  )
    return undefined

  return contact.external_identifiers.find(
    (identifier) => identifier.source_fk_id === firstSourceId,
  )
}

const getExternalIdFromContactFields = (
  contact: LineExtensionContact | undefined,
  externalIdField: string = 'external_id_value',
  allowAnySourceFallback: boolean = false,
): string | undefined => {
  const externalIdentifier = getMatchingExternalIdentifier(contact)
  const fromMatchingSource = (
    externalIdentifier as Record<string, string> | undefined
  )?.[externalIdField]

  if (fromMatchingSource) {
    return fromMatchingSource
  }

  // If source matching fails for v2 data, fall back to any populated external identifier field.
  // This keeps deeplinks stable when source arrays are out of sync.
  if (
    allowAnySourceFallback &&
    isV2Contact(contact) &&
    contact.external_identifiers?.length
  ) {
    for (const identifier of contact.external_identifiers) {
      const value = (identifier as Record<string, string>)[externalIdField]
      if (value) {
        return value
      }
    }
  }

  return (contact as unknown as Record<string, string> | undefined)?.[
    externalIdField
  ]
}

export const getContactFromWebSocketMessage = (
  message: LineExtensionPayload,
  isGrove: boolean,
): LineExtensionContact | undefined => {
  if (isGrove) {
    return message._mango_contacts?.find((contact) => contact?.version === 2)
  }
  return message._mango_contacts?.find((contact) => contact?.version !== 2)
}

/**
 * Finds the first source and then uses the specified field to get the external ID.
 * Falls back to the specified field, or the deprecated contact_external_id field for backwards compatibility with legacy integration data.
 */
export const getContactExternalId = (
  contact: LineExtensionContact | undefined,
  externalIdField: string = 'external_id_value',
): string | undefined => {
  return (
    getExternalIdFromContactFields(contact, externalIdField, false) ||
    contact?.contact_external_id // These fallbacks are for backwards compatibility with legacy integration data
  )
}

/**
 * Gets the contact external ID used for partner deeplinks.
 * For v2 contacts this intentionally avoids falling back to contact_external_id so we never
 * substitute display IDs when external_id_value is expected.
 */
export const getContactExternalIdForDeeplink = (
  contact: LineExtensionContact | undefined,
  externalIdField: string = 'external_id_value',
): string | undefined => {
  const externalId = getExternalIdFromContactFields(
    contact,
    externalIdField,
    true,
  )
  if (externalId) {
    return externalId
  }

  // Keep legacy support for non-v2 contacts that only have contact_external_id.
  if (contact && !isV2Contact(contact)) {
    return contact.contact_external_id
  }

  return undefined
}

// Token encoders receive the value and whether the token sits in the query or
// fragment (both are parsed with form-urlencoded rules).
type TokenEncode = (value: string, isFormEncoded: boolean) => string

/**
 * Encodes a token value, context-aware. Token values come from outside the app
 * (caller ID, partner integration data), so structural characters (&, #, ?,
 * whitespace) must be neutralized to prevent URL-structure injection.
 *   - Path segment: keep '+' literal (TrackerPhone and other non-decoding scheme
 *     handlers read the path verbatim; encoding '+' to '%2B' broke them).
 *   - Query or fragment: keep '%2B' so the '+' survives (both are read with
 *     form-urlencoded rules, which decode a literal '+' to a space).
 */
const encodeTokenValue: TokenEncode = (value, isFormEncoded) => {
  const encoded = encodeURIComponent(value)
  return isFormEncoded ? encoded : encoded.replace(/%2B/g, '+')
}

/** Tokens allowed in a partner's `partner_deep_link_pattern`. */
export const PARTNER_DEEP_LINK_TOKENS = {
  contactId: { token: '@@contactId', encode: encodeTokenValue },
  callerNumber: { token: '@@callerNumber', encode: encodeTokenValue },
} as const

type PartnerDeepLinkValues = {
  [K in keyof typeof PARTNER_DEEP_LINK_TOKENS]?: string
}

/**
 * Substitutes every supported token in the pattern. A missing value becomes an
 * empty string, so no raw "@@token" leaks into the launched URL. Each token is
 * encoded per its position: a token after the first '?' or '#' is form-encoded,
 * anything before is treated as a path segment.
 */
export const buildPartnerDeepLink = (
  pattern: string | undefined,
  values: PartnerDeepLinkValues,
): string | undefined => {
  if (!pattern) {
    return undefined
  }
  return Object.entries(PARTNER_DEEP_LINK_TOKENS).reduce((url, [key, spec]) => {
    const value = values[key as keyof PartnerDeepLinkValues] ?? ''
    // Boundary is judged against the current url (offset is relative to it).
    // Encoding escapes '?' and '#' in substituted values, so an earlier token
    // cannot shift it.
    const boundary = url.search(/[?#]/)
    return url.replaceAll(spec.token, (_match, offset: number) => {
      const isFormEncoded = boundary !== -1 && offset > boundary
      return spec.encode(value, isFormEncoded)
    })
  }, pattern)
}

/**
 * Whether a deep link can be built: the pattern must reference at least one token
 * we have a value for. This lets a `@@callerNumber`-only pattern work with no
 * matched contact (e.g. TDO keys the patient file off the caller number).
 */
export const canBuildPartnerDeepLink = (
  pattern: string | undefined,
  values: PartnerDeepLinkValues,
): boolean => {
  if (!pattern) {
    return false
  }
  return Object.entries(PARTNER_DEEP_LINK_TOKENS).some(
    ([key, { token }]) =>
      pattern.includes(token) && !!values[key as keyof PartnerDeepLinkValues],
  )
}

/**
 * Returns a user-facing patient ID. Prefers patient_display_id (customer-friendly)
 * from external_identifiers, falling back to the system external ID.
 * Includes any-source fallback for v2 contacts when source arrays are out of sync.
 */
export const getContactDisplayId = (
  contact: LineExtensionContact | undefined,
  externalIdField: string = 'external_id_value',
): string | undefined => {
  const patientDisplayId = getExternalIdFromContactFields(
    contact,
    'patient_display_id',
    true,
  )
  if (patientDisplayId) {
    return patientDisplayId
  }

  const externalId = getExternalIdFromContactFields(
    contact,
    externalIdField,
    true,
  )
  if (externalId) {
    return externalId
  }

  return contact?.contact_external_id
}

/**
 * Gets the contact_status from external_identifiers by matching the first source.
 * This replaces the deprecated patient_data.status field.
 */
export const getContactStatus = (
  contact: LineExtensionContact | undefined,
): string | undefined => {
  const status = getMatchingExternalIdentifier(contact)?.contact_status
  if (!status) return undefined
  return (
    status.charAt(0).toLocaleUpperCase() + status.slice(1).toLocaleLowerCase()
  )
}

/**
 * Gets the preferred name, falling back to given_name, then full_name.
 * For v2 contacts: Uses root-level fields only (preferred_name, given_name, full_name).
 * For v1 contacts: Uses nested name object (name.preferred, name.first, name.full).
 */
export const getContactPreferredName = (
  contact: LineExtensionContact | undefined,
): string | undefined => {
  if (!contact) return undefined
  if (isV2Contact(contact)) {
    return contact.preferred_name || contact.given_name || contact.full_name
  }
  return contact.name?.preferred || contact.name?.first || contact.name?.full
}

/**
 * Gets the full name.
 * For v2 contacts: Uses root-level field only (full_name).
 * For v1 contacts: Uses nested name object (name.full).
 */
export const getContactFullName = (
  contact: LineExtensionContact | undefined,
): string | undefined => {
  if (!contact) return undefined
  if (isV2Contact(contact)) {
    return contact.full_name
  }
  return contact.name?.full
}

/**
 * Gets the given name (first name).
 * For v2 contacts: Uses root-level field only (given_name).
 * For v1 contacts: Uses nested name object (name.first).
 */
export const getContactGivenName = (
  contact: LineExtensionContact | undefined,
): string | undefined => {
  if (!contact) return undefined
  if (isV2Contact(contact)) {
    return contact.given_name
  }
  return contact.name?.first
}

/**
 * Gets the family name (last name).
 * For v2 contacts: Uses root-level field only (family_name).
 * For v1 contacts: Uses nested name object (name.last).
 */
export const getContactFamilyName = (
  contact: LineExtensionContact | undefined,
): string | undefined => {
  if (!contact) return undefined
  if (isV2Contact(contact)) {
    return contact.family_name
  }
  return contact.name?.last
}

// ============================================================================
// Phone Call Display Helper Functions
// ============================================================================

/**
 * Get display caller information based on preference.
 *
 * @param call - The phone call object
 * @param preferSipCallerId - If true, prefer SIP data; if false, prefer WebSocket contact data
 * @returns Object with number and name to display
 *
 * TODO: When adding user setting to control this behavior, replace the boolean parameter usage with:
 *   const { settings: { webPhone: { preferSipCallerIdOverContact } } } = useSettingsState()
 *   const displayInfo = getDisplayCallerInfo(call, preferSipCallerIdOverContact)
 */
export function getDisplayCallerInfo(
  call: PhoneCall,
  preferSipCallerId: boolean,
): { number: string; name: string | null } {
  // Prefer SIP caller ID if requested and available
  if (preferSipCallerId && call.sipCallerId) {
    return {
      number: call.sipCallerId.number,
      name: call.sipCallerId.name,
    }
  }

  // Default: prefer WebSocket contact data
  if (call.contactInfo) {
    return {
      number: call.contactInfo.phone_number || call.number,
      name: getContactFullName(call.contactInfo) || call.name,
    }
  }

  // Fallback to sipCallerId or existing fields
  return {
    number: call.sipCallerId?.number || call.number,
    name: call.sipCallerId?.name || call.name,
  }
}
